import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAllLearners, useAllGroups } from '../../services/learners';

export interface AttendeeSelection {
  learner_ids: string[];
  learner_group_ids: string[];
}

interface AttendeePickerProps {
  value: AttendeeSelection;
  onChange: (next: AttendeeSelection) => void;
  max?: number;
  disabled?: boolean;
  placeholder?: string;
}

const AttendeePicker: React.FC<AttendeePickerProps> = ({
  value,
  onChange,
  max = 50,
  disabled = false,
  placeholder = 'Write an email or find a department in the dropdown',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data: learners = [], isLoading: loadingLearners } = useAllLearners();
  const { data: groups = [], isLoading: loadingGroups } = useAllGroups();

  const totalSelected =
    value.learner_ids.length + value.learner_group_ids.length;

  const filteredLearners = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return learners;
    return learners.filter((l) => {
      return (
        l.email.toLowerCase().includes(q) ||
        `${l.first_name} ${l.last_name}`.toLowerCase().includes(q)
      );
    });
  }, [learners, query]);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, query]);

  const learnerById = useMemo(() => {
    const map = new Map<string, (typeof learners)[number]>();
    for (const l of learners) map.set(l.id, l);
    return map;
  }, [learners]);

  const groupById = useMemo(() => {
    const map = new Map<string, (typeof groups)[number]>();
    for (const g of groups) map.set(g.id, g);
    return map;
  }, [groups]);

  const atMax = totalSelected >= max;

  const toggleLearner = (id: string) => {
    const has = value.learner_ids.includes(id);
    if (has) {
      onChange({ ...value, learner_ids: value.learner_ids.filter((x) => x !== id) });
    } else {
      if (atMax) return;
      onChange({ ...value, learner_ids: [...value.learner_ids, id] });
    }
  };

  const toggleGroup = (id: string) => {
    const has = value.learner_group_ids.includes(id);
    if (has) {
      onChange({
        ...value,
        learner_group_ids: value.learner_group_ids.filter((x) => x !== id),
      });
    } else {
      if (atMax) return;
      onChange({
        ...value,
        learner_group_ids: [...value.learner_group_ids, id],
      });
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-2 border border-border-primary/30 bg-surface-primary text-left hover:border-border-primary/60 transition-colors disabled:opacity-50"
      >
        <span className="text-sm text-text-tertiary truncate">
          {totalSelected === 0
            ? placeholder
            : `${totalSelected} attendee${totalSelected === 1 ? '' : 's'} selected`}
        </span>
        <ChevronDownIcon
          className={['w-4 h-4 text-text-secondary shrink-0 transition-transform', open ? 'rotate-180' : ''].join(' ')}
        />
      </button>

      {/* Selected chips */}
      {totalSelected > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.learner_group_ids.map((id) => {
            const g = groupById.get(id);
            return (
              <span
                key={`g-${id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/12 text-primary-700 dark:text-primary-200 text-xs font-medium"
              >
                {g?.name ?? 'Group'}
                <button
                  type="button"
                  onClick={() => toggleGroup(id)}
                  className="hover:bg-primary-500/24 rounded-full p-0.5 active:scale-90 transition-transform"
                  aria-label={`Remove ${g?.name ?? 'group'}`}
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          {value.learner_ids.map((id) => {
            const l = learnerById.get(id);
            return (
              <span
                key={`l-${id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-elevated text-text-primary text-xs font-medium border border-border-primary/20"
              >
                {l ? `${l.first_name} ${l.last_name}` : 'Learner'}
                <button
                  type="button"
                  onClick={() => toggleLearner(id)}
                  className="hover:bg-action-hover rounded-full p-0.5 active:scale-90 transition-transform"
                  aria-label={`Remove ${l?.first_name ?? 'learner'}`}
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {open && (
        <div className="rounded-2 border border-border-primary/20 bg-surface-primary shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border-primary/15">
            <MagnifyingGlassIcon className="w-4 h-4 text-text-tertiary" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search learners or groups…"
              className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary outline-none"
            />
            {atMax && (
              <span className="text-[11px] font-medium text-warning-700">
                Max {max}
              </span>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-border-primary/10">
            {(loadingGroups || filteredGroups.length > 0) && (
              <div>
                <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-text-tertiary">
                  Groups
                </div>
                {loadingGroups ? (
                  <div className="px-3 py-3 text-xs text-text-tertiary">Loading…</div>
                ) : (
                  filteredGroups.map((g) => {
                    const checked = value.learner_group_ids.includes(g.id);
                    return (
                      <label
                        key={g.id}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-action-hover"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleGroup(g.id)}
                          disabled={!checked && atMax}
                          className="w-4 h-4 accent-primary-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-text-primary truncate">
                            {g.name}
                          </div>
                          <div className="text-xs text-text-tertiary">
                            {g.members_count} member{g.members_count === 1 ? '' : 's'}
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            )}
            <div>
              <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-text-tertiary">
                Learners
              </div>
              {loadingLearners ? (
                <div className="px-3 py-3 text-xs text-text-tertiary">Loading…</div>
              ) : filteredLearners.length === 0 ? (
                <div className="px-3 py-3 text-xs text-text-tertiary">No matches</div>
              ) : (
                filteredLearners.map((l) => {
                  const checked = value.learner_ids.includes(l.id);
                  return (
                    <label
                      key={l.id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-action-hover"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleLearner(l.id)}
                        disabled={!checked && atMax}
                        className="w-4 h-4 accent-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">
                          {l.first_name} {l.last_name}
                        </div>
                        <div className="text-xs text-text-tertiary truncate">
                          {l.email}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
          <div className="px-3 py-2 border-t border-border-primary/15 flex items-center justify-between text-xs text-text-tertiary">
            <span>
              {totalSelected} / {max} selected
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-primary-500 font-medium hover:underline"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendeePicker;
